/**
 * Financials API Client
 *
 * Expense endpoints are scoped to a farm via /api/v1/farms/:farmId/expenses.
 * Cycle financial endpoints use /api/v1/farms/:farmId/cycles/:cycleId/financials.
 * Farm summary and comparison use /api/v1/farms/:farmId/financials/*.
 */

import { api } from './api';
import type { ApiResponse, ApiListResponse } from '@/types/api';
import type {
  ExpenseCategory,
  ExpenseCreate,
  ExpenseUpdate,
  Expense,
  AdjustmentCreate,
  Adjustment,
  CycleFinancials,
  CostBreakdown,
  FarmFinancialSummary,
  CycleComparison,
} from '@/types/financials';

export const financialsApi = {
  // ===== Categories (global) =====

  listCategories: () =>
    api.get<ApiListResponse<ExpenseCategory>>('/api/v1/expense-categories'),

  // ===== Expenses (farm-scoped) =====

  createExpense: (farmId: string, data: ExpenseCreate) =>
    api.post<ApiResponse<Expense>>(
      `/api/v1/farms/${farmId}/expenses`,
      data
    ),

  listExpenses: (farmId: string, params?: Record<string, string>) =>
    api.get<ApiListResponse<Expense>>(
      `/api/v1/farms/${farmId}/expenses`,
      { params }
    ),

  updateExpense: (farmId: string, expenseId: string, data: ExpenseUpdate) =>
    api.patch<ApiResponse<Expense>>(
      `/api/v1/farms/${farmId}/expenses/${expenseId}`,
      data
    ),

  deleteExpense: (farmId: string, expenseId: string) =>
    api.delete<ApiResponse<null>>(
      `/api/v1/farms/${farmId}/expenses/${expenseId}`
    ),

  // ===== Adjustments (cycle-scoped) =====

  createAdjustment: (farmId: string, cycleId: string, data: AdjustmentCreate) =>
    api.post<ApiResponse<Adjustment>>(
      `/api/v1/farms/${farmId}/cycles/${cycleId}/adjustments`,
      data
    ),

  listAdjustments: (farmId: string, cycleId: string) =>
    api.get<ApiListResponse<Adjustment>>(
      `/api/v1/farms/${farmId}/cycles/${cycleId}/adjustments`
    ),

  // ===== Cycle financials =====

  getCycleFinancials: (farmId: string, cycleId: string) =>
    api.get<ApiResponse<CycleFinancials>>(
      `/api/v1/farms/${farmId}/cycles/${cycleId}/financials`
    ),

  getCostBreakdown: (farmId: string, cycleId: string, groupBy?: string) =>
    api.get<ApiResponse<CostBreakdown>>(
      `/api/v1/farms/${farmId}/cycles/${cycleId}/costs/breakdown`,
      { params: groupBy ? { group_by: groupBy } : undefined }
    ),

  // ===== Farm summary =====

  getFarmSummary: (farmId: string, params?: Record<string, string>) =>
    api.get<ApiResponse<FarmFinancialSummary>>(
      `/api/v1/farms/${farmId}/financials/summary`,
      { params }
    ),

  // ===== Comparison =====

  getComparison: (farmId: string, cycleIds: string[]) =>
    api.get<ApiResponse<CycleComparison>>(
      `/api/v1/farms/${farmId}/financials/comparison`,
      { params: { cycle_ids: cycleIds.join(',') } }
    ),

  // ===== Export =====

  exportReport: (farmId: string, params: Record<string, string>) =>
    api.get<Blob>(
      `/api/v1/farms/${farmId}/financials/export`,
      { params, responseType: 'blob' }
    ),
};
